import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { bookingService } from "@/services/bookingService";
import { formatMoney } from "@/lib/money";
import type { ReceiptAnalyzeResponse } from "@/types";

import type { BookingListRow } from "./bookingViewModel";

interface ReceiptConfirmState {
  bookingId: string;
  storagePath: string;
  analyzeResult: ReceiptAnalyzeResponse;
}

interface ManualAmountState {
  bookingId: string;
  storagePath: string;
}

interface ReceiptOcrFailureState {
  booking: BookingListRow;
  storagePath: string;
  message: string;
  manualAmountRequired: boolean;
  remainingValidationAttempts?: number;
  maxValidationAttempts?: number;
}

interface ReceiptAlertState {
  bookingId: string;
  title: string;
  message: string;
}

interface UseReceiptFlowOptions {
  /** Customers capture + confirm in one call; partners upload, analyze and register. */
  customerMode: boolean;
}

/**
 * Receipt upload → OCR → confirm/reject → manual total flow shared by every
 * surface that lets a booking's receipt be uploaded. State is keyed by booking
 * so list surfaces can run it for several cards.
 */
export function useReceiptFlow({ customerMode }: UseReceiptFlowOptions) {
  const { t } = useTranslation("bookings");
  const queryClient = useQueryClient();

  const [processingByBookingId, setProcessingByBookingId] = useState<
    Record<string, boolean>
  >({});
  const [fileInputResetKey, setFileInputResetKey] = useState<
    Record<string, number>
  >({});
  const [receiptConfirm, setReceiptConfirm] =
    useState<ReceiptConfirmState | null>(null);
  const [manualAmountEntry, setManualAmountEntry] =
    useState<ManualAmountState | null>(null);
  const [receiptOcrFailure, setReceiptOcrFailure] =
    useState<ReceiptOcrFailureState | null>(null);
  const [receiptAlert, setReceiptAlert] = useState<ReceiptAlertState | null>(
    null,
  );
  const [confirmingReceipt, setConfirmingReceipt] = useState(false);
  const [submittingManualAmount, setSubmittingManualAmount] = useState(false);

  // Remounts the booking's file input so the same file can be picked again.
  const resetFileInput = (bookingId: string) => {
    setFileInputResetKey((prev) => ({
      ...prev,
      [bookingId]: (prev[bookingId] ?? 0) + 1,
    }));
  };

  const invalidateBooking = (bookingId: string) => {
    void queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
    void queryClient.invalidateQueries({ queryKey: ["bookings"] });
  };

  const showConfirmError = (bookingId: string) => {
    setReceiptAlert({
      bookingId,
      title: t("receiptConfirmErrorTitle"),
      message: t("receiptConfirmErrorMessage"),
    });
  };

  const uploadReceipt = async (booking: BookingListRow, file: File) => {
    const bookingId = booking.id;

    setProcessingByBookingId((prev) => ({ ...prev, [bookingId]: true }));
    setReceiptOcrFailure(null);
    let uploadedPath: string | undefined;

    try {
      if (customerMode) {
        const captureResult = await bookingService.captureCustomerReceipt(
          bookingId,
          file,
        );
        if (!captureResult.ocr_success) {
          setReceiptAlert({
            bookingId,
            title: t("receiptOcrUnreadableTitle"),
            message:
              captureResult.error_message || t("receiptOcrUnreadableFallback"),
          });
          return;
        }
        setReceiptConfirm({
          bookingId,
          storagePath: captureResult.storage_path,
          analyzeResult: captureResult,
        });
        return;
      }

      const uploadResult = await bookingService.uploadSellerReceipt(
        bookingId,
        file,
      );
      uploadedPath = uploadResult.path;

      const analyzeResult = await bookingService.analyzeSellerReceipt(
        bookingId,
        uploadResult.path,
      );

      if (!analyzeResult.ocr_success) {
        setReceiptOcrFailure({
          booking,
          storagePath: uploadResult.path,
          message:
            analyzeResult.error_message || t("receiptOcrUnreadableFallback"),
          manualAmountRequired: analyzeResult.manual_amount_required === true,
          remainingValidationAttempts:
            analyzeResult.remaining_validation_attempts,
          maxValidationAttempts: analyzeResult.max_validation_attempts,
        });
        return;
      }

      setReceiptConfirm({
        bookingId,
        storagePath: uploadResult.path,
        analyzeResult,
      });
    } catch (e) {
      if (!customerMode) {
        setReceiptOcrFailure({
          booking,
          storagePath: uploadedPath ?? booking.latestUploadPath ?? "",
          message: t("receiptUploadErrorMessage"),
          manualAmountRequired: booking.manualAmountRequired === true,
        });
      } else {
        setReceiptAlert({
          bookingId,
          title: t("receiptUploadErrorTitle"),
          message: t("receiptUploadErrorMessage"),
        });
      }
      console.error(e);
    } finally {
      setProcessingByBookingId((prev) => ({ ...prev, [bookingId]: false }));
    }
  };

  const confirmReceipt = async () => {
    if (!receiptConfirm) return;

    const { bookingId, storagePath, analyzeResult } = receiptConfirm;
    const totalCents = analyzeResult.extracted_total_cents ?? 0;

    setConfirmingReceipt(true);
    try {
      if (customerMode) {
        const result = await bookingService.confirmCustomerReceipt(
          bookingId,
          storagePath,
          totalCents,
        );
        toast.success(
          result.already_processed
            ? t("toastReceiptAlready")
            : t("toastPointsAwarded", { points: result.awarded_points }),
          { duration: 5000 },
        );
      } else {
        await bookingService.registerSellerReceipt(
          bookingId,
          storagePath,
          totalCents,
          "OCR",
        );
        toast.success(
          t("toastPartnerUploaded", {
            total: analyzeResult.extracted_total_display,
          }),
        );
      }
      invalidateBooking(bookingId);
      void queryClient.invalidateQueries({ queryKey: ["customerBookings"] });
    } catch (e) {
      showConfirmError(bookingId);
      console.error(e);
    } finally {
      setConfirmingReceipt(false);
    }
    setReceiptConfirm(null);
  };

  const rejectReceipt = async () => {
    if (!receiptConfirm) return;

    const { bookingId, storagePath } = receiptConfirm;
    if (customerMode) {
      resetFileInput(bookingId);
      setReceiptConfirm(null);
      return;
    }

    setConfirmingReceipt(true);
    try {
      const result = await bookingService.rejectSellerReceipt(
        bookingId,
        storagePath,
      );
      invalidateBooking(bookingId);
      if (result.manual_amount_required) {
        setManualAmountEntry({ bookingId, storagePath });
      } else {
        resetFileInput(bookingId);
      }
    } catch (e) {
      showConfirmError(bookingId);
      console.error(e);
    } finally {
      setConfirmingReceipt(false);
      setReceiptConfirm(null);
    }
  };

  const submitManualAmount = async (totalCents: number) => {
    if (!manualAmountEntry) return;

    const { bookingId, storagePath } = manualAmountEntry;
    setSubmittingManualAmount(true);
    try {
      await bookingService.registerSellerReceipt(
        bookingId,
        storagePath,
        totalCents,
        "MANUAL",
      );
      toast.success(
        t("toastPartnerManualAmount", { total: formatMoney(totalCents) }),
      );
      invalidateBooking(bookingId);
      setManualAmountEntry(null);
    } catch (e) {
      showConfirmError(bookingId);
      console.error(e);
    } finally {
      setSubmittingManualAmount(false);
    }
  };

  const openManualAmount = (bookingId: string, storagePath: string) => {
    setManualAmountEntry({ bookingId, storagePath });
  };

  const closeManualAmount = () => {
    setManualAmountEntry(null);
  };

  /** From the OCR failure modal: type the total for the receipt just uploaded. */
  const switchOcrFailureToManualAmount = () => {
    if (!receiptOcrFailure) return;

    const path =
      receiptOcrFailure.storagePath ||
      receiptOcrFailure.booking.latestUploadPath;
    if (!path) return;
    setManualAmountEntry({
      bookingId: receiptOcrFailure.booking.id,
      storagePath: path,
    });
    setReceiptOcrFailure(null);
  };

  const closeAlert = () => {
    if (!receiptAlert) return;

    resetFileInput(receiptAlert.bookingId);
    setReceiptAlert(null);
  };

  return {
    processingByBookingId,
    fileInputResetKey,
    receiptConfirm,
    manualAmountEntry,
    receiptOcrFailure,
    receiptAlert,
    confirmingReceipt,
    submittingManualAmount,
    uploadReceipt,
    confirmReceipt,
    rejectReceipt,
    submitManualAmount,
    openManualAmount,
    closeManualAmount,
    switchOcrFailureToManualAmount,
    closeAlert,
  };
}

export type ReceiptFlow = ReturnType<typeof useReceiptFlow>;
